import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './WebchatToolsFilters.module.css';
import type { ToolOption } from '../../../../../types/types';
import { TagsEnv } from '../../../../../ui/Tags/TagsEnv';

type FilterKey = 'connector' | 'method' | 'tag';

interface Props {
  options: ToolOption[];
  onFilter: (filtered: ToolOption[]) => void;
}

export default function WebchatToolsFilters({ options, onFilter }: Props) {
  const [selectedConnectors, setSelectedConnectors] = useState<string[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const connectors = useMemo(() => {
    const seen = new Map<string, [string, string]>();
    for (const opt of options) {
      if (opt.connectorId && !seen.has(opt.connectorId)) {
        seen.set(opt.connectorId, [opt.connectorName ?? opt.connectorId, opt.environment ?? '']);
      }
    }
    return Array.from(seen.entries()).map(([id, [name, environment]]) => ({ id, name, environment }));
  }, [options]);

  const hasOther = useMemo(() => options.some(o => !o.connectorId), [options]);

  const methods = useMemo(() => {
    const seen = new Set<string>();
    for (const opt of options) { if (opt.httpMethod) seen.add(opt.httpMethod); }
    return Array.from(seen);
  }, [options]);

  const tags = useMemo(() => {
    const seen = new Set<string>();
    for (const opt of options) { for (const t of opt.tags ?? []) seen.add(t); }
    return Array.from(seen).sort();
  }, [options]);

  // Apply filters and notify parent whenever options or selections change
  useEffect(() => {
    const filtered = options.filter(opt => {
      if (selectedConnectors.length > 0) {
        const matches = selectedConnectors.some(c =>
          c === 'other' ? !opt.connectorId : opt.connectorId === c
        );
        if (!matches) return false;
      }
      if (selectedMethods.length > 0 && !selectedMethods.includes(opt.httpMethod ?? '')) return false;
      if (selectedTags.length > 0 && !opt.tags?.some(t => selectedTags.includes(t))) return false;
      return true;
    });
    onFilter(filtered);
  }, [options, selectedConnectors, selectedMethods, selectedTags]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleConnector = (id: string) =>
    setSelectedConnectors(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const toggleMethod = (m: string) =>
    setSelectedMethods(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  const toggleTag = (t: string) =>
    setSelectedTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const toggleOpen = (key: FilterKey) =>
    setOpenFilter(prev => prev === key ? null : key);

  if (connectors.length === 0 && methods.length === 0 && tags.length === 0) return null;

  return (
    <div className={styles.filterBar} ref={barRef}>
      Tools Filters:
      {connectors.length > 0 && (
        <div className={styles.filterDropdown}>
          <button
            type="button"
            className={`${styles.filterBtn} ${selectedConnectors.length > 0 ? styles.filterBtnActive : ''}`}
            onClick={() => toggleOpen('connector')}
          >
            Connector
            {selectedConnectors.length > 0 && <span className={styles.filterBadge}>{selectedConnectors.length}</span>}
            <span className={styles.filterChevron}>▾</span>
          </button>
          {openFilter === 'connector' && (
            <div className={styles.filterPanel}>
              <div className={styles.filterPillGroup}>
                {connectors.map(c => (
                  <button key={c.id} type="button" className={`${styles.filterPill} ${selectedConnectors.includes(c.id) ? styles.filterPillActive : ''}`} onClick={() => toggleConnector(c.id)}>
                    {c.name} <TagsEnv env={c.environment as 'prod' | 'test' | 'dev' | undefined} isToolDisabledByConnector={false}/> {selectedConnectors.includes(c.id) ? ' ×' : ''}
                  </button>
                ))}
                {hasOther && (
                  <button type="button" className={`${styles.filterPill} ${selectedConnectors.includes('other') ? styles.filterPillActive : ''}`} onClick={() => toggleConnector('other')}>
                    Other{selectedConnectors.includes('other') ? ' ×' : ''}
                  </button>
                )}
              </div>
              {selectedConnectors.length > 0 && (
                <div className={styles.filterPanelFooter}>
                  <button type="button" className={styles.filterClearAll} onClick={() => setSelectedConnectors([])}>Clear all</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {methods.length > 0 && (
        <div className={styles.filterDropdown}>
          <button
            type="button"
            className={`${styles.filterBtn} ${selectedMethods.length > 0 ? styles.filterBtnActive : ''}`}
            onClick={() => toggleOpen('method')}
          >
            Method
            {selectedMethods.length > 0 && <span className={styles.filterBadge}>{selectedMethods.length}</span>}
            <span className={styles.filterChevron}>▾</span>
          </button>
          {openFilter === 'method' && (
            <div className={styles.filterPanel}>
              <div className={styles.filterPillGroup}>
                {methods.map(m => (
                  <button key={m} type="button" className={`${styles.filterPill} ${selectedMethods.includes(m) ? styles.filterPillActive : ''}`} onClick={() => toggleMethod(m)}>
                    {m}{selectedMethods.includes(m) ? ' ×' : ''}
                  </button>
                ))}
              </div>
              {selectedMethods.length > 0 && (
                <div className={styles.filterPanelFooter}>
                  <button type="button" className={styles.filterClearAll} onClick={() => setSelectedMethods([])}>Clear all</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tags.length > 0 && (
        <div className={styles.filterDropdown}>
          <button
            type="button"
            className={`${styles.filterBtn} ${selectedTags.length > 0 ? styles.filterBtnActive : ''}`}
            onClick={() => toggleOpen('tag')}
          >
            Tag
            {selectedTags.length > 0 && <span className={styles.filterBadge}>{selectedTags.length}</span>}
            <span className={styles.filterChevron}>▾</span>
          </button>
          {openFilter === 'tag' && (
            <div className={styles.filterPanel}>
              <div className={styles.filterPillGroup}>
                {tags.map(t => (
                  <button key={t} type="button" className={`${styles.filterPill} ${selectedTags.includes(t) ? styles.filterPillActive : ''}`} onClick={() => toggleTag(t)}>
                    {t}{selectedTags.includes(t) ? ' ×' : ''}
                  </button>
                ))}
              </div>
              {selectedTags.length > 0 && (
                <div className={styles.filterPanelFooter}>
                  <button type="button" className={styles.filterClearAll} onClick={() => setSelectedTags([])}>Clear all</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
